import { Component, Element, Host, Prop, Watch, h } from '@stencil/core';
import configs, {
  ConfigFunction,
  ContainerConfig,
  ContainerType,
} from './config/container-configs';

/**
 * @description Provides a 12 column grid system for page layout.
 * @use
 * @ssh
 * @version NV
 * @since 5.9.0
 */
@Component({
  tag: 'hp6-container',
  styleUrl: 'container.scss',
  shadow: false, // Per single-tag policy, shadow is not necessary since this does not add any DOM elements
})
export class Container implements ContainerConfig {
  /**
   * Optionally specify a container configuration. See readme.
   */
  @Prop({ reflect: true }) typeSelection?: ContainerType;

  /**
   * Number of columns to render for the type selection.
   */
  @Prop({ mutable: true }) typeColumns?: 1 | 2 | 3 | 4 | 6 | 8 | 10 | 12;

  /**
   * Number of columns to render at xsmall up (optional).
   */
  @Prop({ mutable: true }) columnsXs?: 1 | 2 | 3 | 4 | 6 | 8 | 10 | 12 = 1;

  /**
   * Number of columns to render at small up (optional).
   */
  @Prop({ mutable: true }) columnsSm?: 1 | 2 | 3 | 4 | 6 | 8 | 10 | 12;

  /**
   * Number of columns to render at medium up (optional).
   */
  @Prop({ mutable: true }) columnsMd?: 1 | 2 | 3 | 4 | 6 | 8 | 10 | 12;

  /**
   * Number of columns to render at large up (optional).
   */
  @Prop({ mutable: true }) columnsLg?: 1 | 2 | 3 | 4 | 6 | 8 | 10 | 12;

  /**
   * Number of columns to offset left and right at xsmall (optional).
   */
  @Prop({ mutable: true }) offsetXs?: 0 | 1 | 2 | 3 | 4 | 5 = 0;

  /**
   * Number of columns to offset left and right at small (optional).
   */
  @Prop({ mutable: true }) offsetSm?: 0 | 1 | 2 | 3 | 4 | 5;

  /**
   * Number of columns to offset left and right at medium (optional).
   */
  @Prop({ mutable: true }) offsetMd?: 0 | 1 | 2 | 3 | 4 | 5;

  /**
   * Number of columns to offset left and right at large (optional).
   */
  @Prop({ mutable: true }) offsetLg?: 0 | 1 | 2 | 3 | 4 | 5;

  /**
   * Size of gutter between rows  (optional).
   */
  @Prop({ mutable: true }) rowGutter: 'none' | 'xs' | 'sm' | 'md' | 'lg' = 'none';

  @Element() host: HTMLElement;

  /**
   * A additional CSS class which a configuration can apply if necessary.
   * Not user configurable.
   */
  layoutClass: string = '';

  @Watch('type')
  @Watch('typeColumns')
  applyType() {
    const { typeSelection, typeColumns } = this;
    if (typeSelection) {
      const func: ConfigFunction = configs[typeSelection as string];
      const defaultConfig = configs.defaults();
      const typeConfig: ContainerConfig = func(typeColumns);

      if (typeConfig) {
        this.applyConfig({
          ...defaultConfig,
          ...typeConfig,
        });
        this.host.className = 'hydrated';
      }
    }
  }

  private applyConfig(config: ContainerConfig) {
    Object.keys(config).map(key => {
      this[key] = config[key];
      return true;
    });
  }

  componentWillLoad() {
    this.applyType();
  }

  get columnClass() {
    const { columnsXs, columnsSm, columnsMd, columnsLg } = this;
    const value = [];
    if (columnsXs) {
      value.push(`columns-xs-${columnsXs}`);
    }
    if (columnsSm) {
      value.push(`columns-sm-${columnsSm}`);
    }
    if (columnsMd) {
      value.push(`columns-md-${columnsMd}`);
    }
    if (columnsLg) {
      value.push(`columns-lg-${columnsLg}`);
    }
    return value.join(' ');
  }

  get offsetClass() {
    const { offsetXs, offsetSm, offsetMd, offsetLg } = this;
    const value = [];
    if (offsetXs) {
      value.push(`offset-xs-${offsetXs}`);
    }
    if (offsetSm) {
      value.push(`offset-sm-${offsetSm}`);
    }
    if (offsetMd) {
      value.push(`offset-md-${offsetMd}`);
    }
    if (offsetLg) {
      value.push(`offset-lg-${offsetLg}`);
    }
    return value.join(' ');
  }

  get gutterClass() {
    const { rowGutter } = this;
    return rowGutter ? `gutter-${rowGutter}` : '';
  }

  render() {
    const { layoutClass, columnClass, offsetClass, gutterClass } = this;
    return <Host class={`${layoutClass} ${columnClass} ${offsetClass} ${gutterClass}`} />;
  }
}
